import React, { useEffect, useRef } from "react";

// Brand palette and page layouts mirror the landing page field (landing/src/pages/index.astro).
export const AGENT_COLORS = {
  research: "#F2B872",
  data: "#5EEAD4",
  code: "#38BDF8",
  seo: "#8DE8A6",
  ux: "#B9A8FF",
  infra: "#94A3B8",
  analytics: "#F7A1C4",
};

const KEYS = Object.keys(AGENT_COLORS);

// Each layout is a w x h tile grid with rects [u0, u1, v0, v1, kind] (half-open). w + h <= PAGE_SPAN.
const PAGE_SPAN = 32;
const LAYOUTS = [
  { w: 14, h: 18, r: [
    [0, 14, 0, 1, "code"], [1, 9, 2, 4, "research"], [10, 13, 2, 7, "ux"], [1, 8, 5, 6, "research"],
    [1, 13, 8, 9, "research"], [1, 11, 10, 11, "research"], [1, 12, 12, 13, "research"], [1, 13, 14, 18, "data"]] },
  { w: 14, h: 18, r: [
    [0, 14, 0, 1, "code"], [2, 12, 2, 4, "ux"],
    [1, 4, 5, 9, "data"], [5, 8, 5, 9, "data"], [9, 12, 5, 9, "data"],
    [1, 4, 10, 14, "data"], [5, 8, 10, 14, "data"], [9, 12, 10, 14, "data"], [0, 14, 16, 17, "code"]] },
  { w: 14, h: 18, r: [
    [0, 14, 0, 1, "code"], [1, 10, 2, 4, "research"], [1, 13, 5, 6, "code"],
    [1, 13, 7, 8, "data"], [1, 13, 9, 10, "data"], [1, 13, 11, 12, "data"], [1, 13, 13, 14, "data"], [4, 10, 16, 18, "infra"]] },
  { w: 16, h: 16, r: [
    [0, 16, 0, 1, "code"], [1, 9, 2, 10, "data"],
    [10, 15, 2, 3, "research"], [10, 14, 4, 5, "research"], [10, 15, 6, 7, "research"], [10, 13, 8, 9, "research"],
    [1, 5, 11, 15, "ux"], [6, 10, 11, 15, "ux"], [11, 15, 11, 15, "ux"]] },
  { w: 18, h: 12, r: [
    [0, 18, 0, 1, "code"], [1, 9, 2, 3, "research"],
    [1, 2, 8, 11, "data"], [3, 4, 6, 11, "data"], [5, 6, 4, 11, "data"], [7, 8, 5, 11, "data"],
    [9, 10, 7, 11, "data"], [11, 12, 5, 11, "data"], [13, 14, 3, 11, "data"], [15, 16, 6, 11, "data"],
    [1, 17, 11, 12, "ux"]] },
  { w: 14, h: 16, r: [
    [0, 14, 0, 1, "code"], [1, 7, 2, 9, "ux"], [8, 13, 2, 3, "research"], [8, 11, 4, 5, "infra"],
    [8, 13, 6, 7, "data"], [8, 12, 8, 9, "data"],
    [1, 13, 11, 12, "research"], [1, 11, 13, 14, "research"], [0, 14, 15, 16, "code"]] },
  { w: 16, h: 14, r: [
    [0, 16, 0, 1, "code"], [0, 16, 2, 6, "research"],
    [1, 4, 7, 9, "analytics"], [5, 8, 7, 9, "analytics"], [9, 12, 7, 9, "analytics"], [13, 16, 7, 9, "analytics"],
    [1, 10, 10, 11, "research"], [1, 8, 12, 13, "research"], [11, 15, 10, 13, "infra"]] },
];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const eo = (x) => 1 - Math.pow(1 - x, 3);
const eio = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

function rgba(hex, alpha) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${clamp(alpha, 0, 1).toFixed(3)})`;
}

// Quiet, text-free version of the landing field: pages assemble from diamond tiles, get audited,
// lift when deployed, then dissolve. Transparent so it sits on any theme background.
const BuilderField = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const amb = document.createElement("canvas");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W = 0, H = 0, a = 0, b = 0, A = 0, B = 0;
    let grid = [];
    let pages = [];
    let sparks = [];

    const diamond = (x, y, dx, dy, c = ctx) => {
      c.beginPath();
      c.moveTo(x - dx, y);
      c.lineTo(x, y - dy);
      c.lineTo(x + dx, y);
      c.lineTo(x, y + dy);
      c.closePath();
    };

    function makePage(pg, t0) {
      const fresh = LAYOUTS.filter((l) => !pages.some((p) => p.layout === l));
      const layout = pick(fresh.length ? fresh : LAYOUTS);
      const { w, h } = layout;
      // Best of 24 random spots: far from the other pages and from the centered onboarding card.
      const pw = PAGE_SPAN * A, ph = PAGE_SPAN * B;
      let best = -1;
      for (let i = 0; i < 24; i++) {
        const cx = rand(pw * 0.2, W - pw * 0.2), cy = rand(ph * 0.3, H - ph * 0.3);
        let d = 2;
        for (const o of pages) if (o !== pg) d = Math.min(d, Math.hypot((cx - o.cx) / pw, (cy - o.cy) / ph));
        const score = d + Math.abs(cx - W / 2) / pw;
        if (score > best) {
          best = score;
          pg.cx = cx;
          pg.cy = cy;
        }
      }
      const c = Math.round((pg.cx - ((w - h) * A) / 2) / A);
      let r = Math.round((pg.cy - ((w + h) * B) / 2) / B);
      if ((r + c) & 1) r++;
      const tiles = [];
      for (const [u0, u1, v0, v1, k] of layout.r)
        for (let u = u0; u < u1; u++)
          for (let v = v0; v < v1; v++)
            tiles.push({ u, v, k, delay: (v / h) * 2 + rand(0, 0.5), ox: rand(-90, 90), oy: -rand(140, 280) });
      tiles.sort((p, q) => p.u + p.v - (q.u + q.v));
      Object.assign(pg, { ax: c * A, ay: r * B, t0, tiles, layout, w, h });
    }

    function setup(t) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = cv.clientWidth;
      H = cv.clientHeight;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Diamond lattice with the logo's spacing (half-width 18, pitch 21 x 12).
      a = clamp(W / 78, 7, 13);
      b = (a * 5) / 9;
      A = (a * 7) / 6;
      B = (a * 2) / 3;
      grid = [];
      for (let r = -2; r <= Math.ceil(H / B) + 2; r++)
        for (let c = -2; c <= Math.ceil(W / A) + 2; c++) if (!((r + c) & 1)) grid.push({ x: c * A, y: r * B });

      // The quiet background lattice never changes, so draw it once.
      amb.width = cv.width;
      amb.height = cv.height;
      const actx = amb.getContext("2d");
      actx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (const g of grid) {
        diamond(g.x, g.y, a * 0.85, b * 0.85, actx);
        actx.fillStyle = `rgba(128,140,160,${(0.04 + Math.random() * 0.05).toFixed(3)})`;
        actx.fill();
      }

      const count = clamp(Math.round((W * H) / (PAGE_SPAN * A * PAGE_SPAN * B * 3)), 1, 4);
      pages = [];
      for (let i = 0; i < count; i++) {
        const pg = {};
        makePage(pg, t - (i * 12.4) / count - rand(0, 1));
        pages.push(pg);
      }
    }

    function block(x, y, h, top, left, right) {
      const aa = a * 0.9, bb = b * 0.9;
      if (h > 0.5) {
        ctx.fillStyle = left;
        ctx.beginPath();
        ctx.moveTo(x - aa, y - h);
        ctx.lineTo(x, y - h + bb);
        ctx.lineTo(x, y + bb);
        ctx.lineTo(x - aa, y);
        ctx.fill();
        ctx.fillStyle = right;
        ctx.beginPath();
        ctx.moveTo(x, y - h + bb);
        ctx.lineTo(x + aa, y - h);
        ctx.lineTo(x + aa, y);
        ctx.lineTo(x, y + bb);
        ctx.fill();
      }
      ctx.fillStyle = top;
      diamond(x, y - h, aa, bb);
      ctx.fill();
    }

    // Each page runs a 12.4s cycle: build (0-3.2), audit scan (3.2-5.6), deploy lift (5.6-6.5),
    // live (to 10.4), dissolve (to 12.4), then a new layout takes its slot.
    function draw(t, dt) {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(amb, 0, 0, W, H);

      if (Math.random() < dt * 20) {
        const g = pick(grid);
        sparks.push({ x: g.x, y: g.y, k: pick(KEYS), t0: t, d: 1.6 });
      }
      sparks = sparks.filter((s) => t - s.t0 < s.d);
      for (const s of sparks) {
        diamond(s.x, s.y, a * 0.85, b * 0.85);
        ctx.fillStyle = rgba(AGENT_COLORS[s.k], 0.4 * Math.sin((Math.PI * (t - s.t0)) / s.d));
        ctx.fill();
      }

      for (const pg of pages) {
        let pt = t - pg.t0;
        if (pt > 12.4) {
          makePage(pg, t);
          pt = 0;
        }
        const lift = pt < 5.6 ? 0 : pt < 10.4 ? eo(Math.min(1, (pt - 5.6) / 0.9)) * b * 1.3 : b * 1.3;
        const scan = ((pt - 3.2) / 2.2) * (pg.h + 6) - 3;
        for (const tl of pg.tiles) {
          const q = clamp((pt - tl.delay) / 0.7, 0, 1);
          if (q <= 0) continue;
          const col = AGENT_COLORS[tl.k];
          const x = pg.ax + (tl.u - tl.v) * A + tl.ox * (1 - eo(q));
          let y = pg.ay + (tl.u + tl.v) * B + tl.oy * (1 - eo(q));
          let al = 0.72 * q;
          if (pt > 10.4) {
            const q2 = clamp((pt - 10.4 - (1 - tl.v / pg.h) * 0.8) / 0.9, 0, 1);
            y -= eio(q2) * 140;
            al *= 1 - q2;
            if (al <= 0.01) continue;
          } else if (pt > 6.5) {
            al = 0.55 + 0.18 * Math.sin(t * 2.2 + tl.u * 0.5 + tl.v * 0.35);
          }
          if (lift > 0.5) {
            diamond(x, y, a * 0.9, b * 0.9);
            ctx.fillStyle = "rgba(0,0,0,0.08)";
            ctx.fill();
          }
          block(x, y, lift, rgba(col, al), rgba(col, al * 0.35), rgba(col, al * 0.2));
          if (pt > 3.2 && pt < 5.6) {
            const seo = Math.abs(tl.v - scan) < 1.3, ux = Math.abs(tl.v - (scan - 3)) < 1.3;
            if (seo || ux) {
              diamond(x, y - lift, a * 0.9, b * 0.9);
              ctx.fillStyle = rgba(seo ? AGENT_COLORS.seo : AGENT_COLORS.ux, seo ? 0.9 : 0.8);
              ctx.fill();
            }
          }
        }
      }
    }

    let clock = 0;
    let last = performance.now();
    let raf = 0;
    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000) * (reduce ? 0.3 : 1);
      last = now;
      clock += dt;
      draw(clock, dt);
      raf = requestAnimationFrame(frame);
    };
    setup(0);
    raf = requestAnimationFrame(frame);
    const observer = new ResizeObserver(() => setup(clock));
    observer.observe(cv);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" className={className} />;
};

export default BuilderField;
